// Attendance manager for a training class (see `features/training-classes.md`)
// — the desktop shell around the shared `AttendanceCalendar`: the same
// week-strip surface the mobile admin view uses (week navigation, per-day
// slots, Present/Absent marking, session deletion), so both surfaces look and
// behave identically and record into the same sessions.

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type {
  Athlete,
  TrainingClass,
  TrainingClassEnrollment,
} from '../domain';
import { useTranslation } from '../state/I18nContext';
import { AttendanceCalendar } from './AttendanceCalendar';

interface TrainingAttendanceDialogProps {
  open: boolean;
  trainingClass: TrainingClass;
  /** Live enrolments of this class (who attendance is tracked for). */
  enrollments: TrainingClassEnrollment[];
  /** The company roster, to resolve enrolled athletes' names. */
  athletes: Athlete[];
  /** Whether the signed-in user may create sessions and mark attendance. */
  canManage: boolean;
  onClose: () => void;
}

export function TrainingAttendanceDialog({
  open,
  trainingClass,
  enrollments,
  athletes,
  canManage,
  onClose,
}: TrainingAttendanceDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('admin.trainingClasses.attendance.title', { name: trainingClass.name })}
      </DialogTitle>
      <DialogContent dividers>
        <AttendanceCalendar
          trainingClass={trainingClass}
          enrollments={enrollments}
          athletes={athletes}
          canManage={canManage}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('admin.common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
